import { useEffect, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { track } from '@mc/track-event'
import { TrackingContainer, VisibilityConfig } from '@mc/visibility'

const className = 'container mc-mt-4 mc-mb-6'

const hasRenderedContent = (element: HTMLElement | null): boolean => {
  if (!element) return false

  // Check if element has any child nodes with actual content
  const hasNodes = element.childNodes.length > 0
  const textContent = element.textContent?.trim() ?? ''
  const hasText = textContent.length > 0
  const hasChildren = element.children.length > 0

  return hasNodes && (hasText || hasChildren)
}

const trackPageSectionViewed = (location: string) =>
  track('Page Section Viewed', {
    location,
  })

export const Section = ({
  children,
  id = '',
  noAnimation = false,
  location,
  config,
  as = 'section',
}: {
  children: React.ReactNode
  id?: string
  noAnimation?: boolean
  location?: string
  config?: VisibilityConfig
  as?: 'section' | 'div'
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [shouldRender, setShouldRender] = useState(true)

  useEffect(() => {
    // Check if the rendered content is actually empty
    if (!hasRenderedContent(contentRef.current)) {
      setShouldRender(false)
    }
  }, [children])

  if (!shouldRender) return null

  const handleFirstVisible = () => {
    if (location) {
      trackPageSectionViewed(location)
    }
  }

  const Wrapper = as
  const MotionWrapper = motion[as]

  return (
    <TrackingContainer onFirstVisible={handleFirstVisible} config={config}>
      {noAnimation ? (
        <Wrapper className={className} id={id}>
          <div ref={contentRef} className='mc-py-4'>
            {children}
          </div>
        </Wrapper>
      ) : (
        <MotionWrapper
          className={className}
          id={id}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div ref={contentRef} className='mc-py-4'>
            {children}
          </div>
        </MotionWrapper>
      )}
    </TrackingContainer>
  )
}
